import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid4 } from 'uuid';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Chip from '@design-system/chip';
import Canvas from '@design-system/canvas';
import TextButton from '@design-system/text-button';
import { createProjectPath } from '@constant/gridgio/route-path';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  flex-direction: column;
`;
const ItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: ${size.MARGIN_2};
`;
const StatusItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
  gap: ${size.MARGIN_1};
`;

const StatusHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
`;

const ItemContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
`;
const TITLE_DESIGN = {
  box: { width: '100%', alignX: 'left' as const },
  text: { size: 'large' as const, color: color.BLACK_2, bold: true },
};
const BODY_DESIGN = {
  box: { width: '100%', alignX: 'left' as const },
  text: { size: 'medium' as const, color: color.GRAY_1 },
};

const Begin: React.FC = () => {
  const navigate = useNavigate();
  const steps = [
    { number: '01', title: '프로젝트', description: '현장별 작업 관리' },
    { number: '02', title: '도면', description: '프로젝트 안에서 PDF·이미지 등록 및 관리' },
    {
      number: '03',
      title: '작업',
      description: '프로젝트에서 생성 · 도면에 손상 기록',
    },
  ];
  const CadStatusItems = [
    { title: '생성 대기', description: '요청 접수' },
    { title: '생성 중', description: '파일 생성' },
    { title: '완료', description: '다운로드 가능' },
    { title: '실패', description: '재확인 필요' },
  ].map((status) => (
    <Canvas key={uuid4()} theme={ETheme.WHITE} width='100%' padding={size.MARGIN_1} border>
      <ItemContainer>
        <TextBlock
          text={status.title}
          design={{ ...TITLE_DESIGN, text: { size: 'medium', color: color.BLACK_2, bold: true } }}
        />
        <Gap height={size.MARGIN_1} />
        <TextBlock text='—' design={TITLE_DESIGN} />
        <Gap height={size.MARGIN_1} />
        <TextBlock
          text={status.description}
          design={{ ...BODY_DESIGN, text: { size: 'small', color: color.GRAY_1 } }}
        />
      </ItemContainer>
    </Canvas>
  ));
  const StepItems = steps.map((step) => (
    <ItemContainer key={uuid4()}>
      <TextBlock
        text={step.number}
        design={{ ...BODY_DESIGN, text: { size: 'small', color: color.CYAN_2, bold: true } }}
      />
      <Gap height={size.MARGIN_1} />
      <TextBlock
        text={step.title}
        design={{ ...TITLE_DESIGN, text: { size: 'medium', color: color.BLACK_2, bold: true } }}
      />
      <Gap height={size.MARGIN_1} />
      <TextBlock text={step.description} design={BODY_DESIGN} />
    </ItemContainer>
  ));
  return (
    <Wrapper>
      <TextBlock
        text='메뉴별 사용 방법과 CAD 생성 현황을 확인하세요.'
        design={{ box: { alignX: 'left' }, text: { size: 'large', color: color.BLACK_5, bold: true } }}
      />
      <Gap height={size.MARGIN_2} />
      <Canvas theme={ETheme.WHITE} width='100%' padding={size.MARGIN_2} border>
        <ItemContainer>
          <StatusHeaderContainer>
            <TextBlock text='CAD 생성 현황' design={{ text: TITLE_DESIGN.text }} />
            <Chip text='준비 중' theme={ETheme.GRAY} size='small' kind='outlined' />
          </StatusHeaderContainer>
          <Gap height={size.MARGIN_1} />
          <TextBlock text='손상 기록 → CAD 파일' design={BODY_DESIGN} />
          <Gap height={size.MARGIN_2} />
          <StatusItemsContainer>{CadStatusItems}</StatusItemsContainer>
          <Gap height={size.MARGIN_2} />
          <TextBlock text='CAD 생성 기능 준비 중' design={BODY_DESIGN} />
        </ItemContainer>
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <Canvas theme={ETheme.WHITE} width='100%' padding={size.MARGIN_2} border>
        <ItemContainer>
          <TextBlock text='사용 안내' design={TITLE_DESIGN} />
          <Gap height={size.MARGIN_2} />
          <ItemsContainer>{StepItems}</ItemsContainer>
          <Gap height={size.MARGIN_2} />
          <TextBlock text='프로젝트에서 도면 등록 · 원본은 여러 프로젝트에서 재사용 · 손상 기록은 작업별 저장' design={BODY_DESIGN} />
          <Gap height={size.MARGIN_2} />
          <TextButton
            text='프로젝트 보기'
            width='180px'
            theme={ETheme.GRAY}
            kind='outlined'
            size='small'
            shape='pill'
            handleClick={() => navigate(createProjectPath('home'))}
          />

        </ItemContainer>
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <Canvas theme={ETheme.WHITE} width='100%' padding={size.MARGIN_2} border>
        <ItemContainer>
          <TextBlock text='참고 사항' design={TITLE_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextBlock text='지원 도면: PDF, JPG, PNG, WebP' design={BODY_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextBlock text='작업 순서: 도면 선택 → 방향·영역 설정 → 손상 기록' design={BODY_DESIGN} />
          <Gap height={size.MARGIN_1} />
          <TextBlock text='DWG 가져오기: 준비 중' design={BODY_DESIGN} />
        </ItemContainer>
      </Canvas>
      <Gap height={size.MARGIN_2} />
    </Wrapper>
  );
};
export default Begin;
